import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Icon, Label } from 'semantic-ui-react';
import {
  switchActiveRole,
  selectUserProfiles,
  selectActiveRole,
  fetchUserProfiles
} from '../slices/userRoleSlice';

/**
 * Helper function to check if a user with a given primary role can switch to a target role
 * @param {string} primaryRole - The user's primary role
 * @param {string} targetRole - The role the user wants to switch to
 * @returns {boolean} Whether the switch is allowed
 */
function canSwitchToRole(primaryRole, targetRole) {
  /**
   * ⛔ `recipient` IS NOT A HAT ANYONE WEARS, in either direction (2026-10-12).
   *
   * The other three are a privilege ladder of PRODUCERS — admin implies bookCreator implies
   * voiceActor, each doing a superset of the next one's work. A recipient is orthogonal: a reader
   * a book creator grants books to, whose entire surface is /listen.
   *
   *  - Never a TARGET, including for admin, whose `return true` below would otherwise hand an
   *    operator a role that exists to identify somebody else. There is nothing to switch INTO —
   *    a recipient's library is their own, not a view of the app.
   *  - Never a SOURCE: a recipient primaryRole falls through to `return false`, so they cannot
   *    reach the producer side. That is the default and it is correct.
   *
   * Mirrors `getRolesFromPrimaryRole` on the server, which returns ['recipient'] alone and is
   * returned BY nothing else. If one of the two ever grows the role and the other does not, the UI
   * and the token disagree — the shape of the role-switch defect fixed in 31cc497.
   */
  if (targetRole === 'recipient' || primaryRole === 'recipient') {
    return false;
  }
  if (primaryRole === 'admin') {
    return true; // Admin can access all PRODUCER roles
  } else if (primaryRole === 'bookCreator') {
    return targetRole === 'bookCreator' || targetRole === 'voiceActor';
  } else if (primaryRole === 'voiceActor') {
    return targetRole === 'voiceActor';
  }
  return false;
}

/**
 * RoleSwitcher component that allows users to switch between different roles
 * @param {Object} props - Component props
 * @param {string} [props.location='navbar'] - Where the component is being rendered ('navbar' or 'dashboard')
 * @returns {JSX.Element} RoleSwitcher component
 */
const RoleSwitcher = ({ location = 'navbar' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profiles = useSelector(selectUserProfiles);
  const activeRole = useSelector(selectActiveRole);
  const userId = useSelector(state => state.user?.profile?.data?.userId);
  const userProfile = useSelector(state => state.user?.profile);

  // Get the user's primary role (must be called before any early returns)
  const primaryRole = useSelector(state => {
    // Try to get from profiles first
    const primaryProfile = profiles?.find(profile => profile.isPrimary);
    if (primaryProfile) return primaryProfile.type;

    // Fallback to user data
    return state.user?.profile?.data?.primaryRole || 'voiceActor';
  });

  // Fetch user profiles when component mounts (must be called before any early returns)
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserProfiles(userId));
    }
  }, [userId, dispatch]);

  // ⓘ A per-render console.log of the signed-in user’s id and roles used to sit here. Removed
  // 2026-10-24: it fired on every render in production, where nobody is reading it, and what it
  // printed is exactly what you would not choose to write to a shared browser console.

  // Don't render if user data isn't loaded yet (after all hooks are called)
  if (!userProfile?.data) {
    console.log('RoleSwitcher: User data not available, rendering placeholder');
    return <div style={{ width: '50px', height: '25px' }} />; // Placeholder
  }

  // Filter available profiles based on primary role
  const availableProfiles = profiles && profiles.length > 0
    ? getDefaultProfilesForUser(primaryRole)
    : profiles.filter(profile => canSwitchToRole(primaryRole, profile.type));

  // Debug: Log the available profiles to understand what we're working with
  console.log('Available profiles before deduplication:', availableProfiles);

  // Ensure unique role types (in case of duplicates)
  const uniqueRoleTypes = [...new Set(availableProfiles.map(profile => profile.type))];

  // Debug: Log the unique role types
  console.log('Unique role types after deduplication:', uniqueRoleTypes);

  // Generate options from unique role types
  const roleOptions = uniqueRoleTypes.map(type => ({
    key: type,
    text: getRoleName(type),
    value: type,
    content: (
      <div style={{ display: 'flex', alignItems: 'center', padding: '5px 0' }}>
        <Icon name={getRoleIcon(type)} style={{ marginRight: '10px' }} />
        <span>{getRoleName(type)}</span>
      </div>
    )
  }));

  // Debug: Log the final role options
  console.log('Final role options:', roleOptions);

  const handleRoleChange = (value) => {
    dispatch(switchActiveRole(value));

    // Redirect to the appropriate landing page
    const rolePaths = {
      admin: '/landing/admin',
      bookCreator: '/landing/book-creator',
      voiceActor: '/landing/voice-actor'
    };

    navigate(rolePaths[value] || '/landing/admin');
  };

  // Determine text color based on location
  const iconclass = location === 'dashboard' ? 'dashMenuRoleSwitcher-icon' : 'dashMenuRoleSwitcher-icon notDashboardIconColor';

  return (
    <div className="role-switcher" style={{
      display: 'flex',
      alignItems: 'center',
      marginRight: '20px',
      backgroundColor: 'transparent',
      // padding: '8px 12px',
      borderRadius: '4px',
      marginTop: '-4px !important'
    }}>
      {/* <Label size="medium" style={{
        marginRight: '-5px',
        backgroundColor: 'transparent',
        color: textColor,
        padding: '0',
        border: 'none',
        fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
        fontSize: '1.0rem',
        lineHeight: 1.5,
        fontWeight: 500,
        letterSpacing: "1px"
      }}>
        <Icon name={getRoleIcon(activeRole)} style={{ marginRight: '5px' }} />
        {getRoleName(activeRole)}
      </Label> */}
      <Dropdown
        icon={null}
        trigger={
          <Icon
            name="ellipsis vertical"
            className={iconclass}
          />
        }
        direction="left"
        floating
        className="icon role-menu"
        options={roleOptions}
        value={activeRole}
        onChange={(e, { value }) => handleRoleChange(value)}
      />
    </div >
  );
};

// Helper function to get a human-readable role name
function getRoleName(roleType) {
  switch (roleType) {
    case 'admin':
      return 'Administrator';
    case 'bookCreator':
      return 'Book Creator';
    case 'voiceActor':
      return 'Voice Actor';
    default:
      return roleType || 'User';
  }
}

// Helper function to get an appropriate icon for each role
function getRoleIcon(roleType) {
  switch (roleType) {
    case 'admin':
      return 'settings';
    case 'bookCreator':
      return 'book';
    case 'voiceActor':
      return 'microphone';
    default:
      return 'user';
  }
}

// Helper function to get default profiles based on highest role
function getDefaultProfilesForUser(highestRole) {
  if (highestRole === 'admin') {
    return [
      { type: 'admin' },
      { type: 'bookCreator' },
      { type: 'voiceActor' }
    ];
  } else if (highestRole === 'bookCreator') {
    return [
      { type: 'bookCreator' },
      { type: 'voiceActor' }
    ];
  } else {
    return [
      { type: 'voiceActor' }
    ];
  }
}

// Helper function to get a color for each role
function getRoleColor(roleType) {
  switch (roleType) {
    case 'admin':
      return '#3d6a87';
    case 'bookCreator':
      return '#2c5d8a';
    case 'voiceActor':
      return '#4a6b8a';
    default:
      return '#555555';
  }
}

export default RoleSwitcher;
